import {isDevelopmentBuild, isPreProd} from "../utils/constants";
import {
    ApolloClient,
    ApolloLink,
    createHttpLink,
    from,
    InMemoryCache,
    Observable,
    ServerError
} from "@apollo/client";
import {setContext} from "@apollo/client/link/context";
import {ErrorResponse, onError} from "@apollo/client/link/error";
import {webService} from "./web";
import tokenService from "../utils/jwtTokenHandler";
import {Nullable, RecommendedProductType, ReviewMediaType} from "../definitions/types";
import * as Sentry from "@sentry/react";

export const ApolloClients = {
    ContentService: 'ContentService',
    ErpService: 'ErpService'
};

const contentServiceSubdomain = isDevelopmentBuild ? 'cspdqa7vjnb6jhn3chyi6374rq' : isPreProd ? 'xleub7gigbfffpoufb2usl4b2i' : 'jdg77jxwzfclzo6l2t2kpnzx2y';
const erpServiceSubdomain = isDevelopmentBuild ? 'vfk6npjuu5hyvddl34qadyotaq' : isPreProd ? 'yhtvlr4csrgq7ooak47wq7orga' : 'acl6wbvf4bhido4lxtskt2f7im';

function logSentry(errorResponse: ErrorResponse) {
    const {graphQLErrors, networkError, operation, forward} = errorResponse;

    // Manually log the error to Sentry if it's a GraphQL or network error
    if (graphQLErrors || networkError) {
        Sentry.withScope(function (scope) {
            // Set the scope to error. By default, it's set to warning.
            scope.setLevel("error");

            // Set the operation name, variables, and query for debugging purposes
            scope.setExtra('operationName', operation.operationName);
            scope.setExtra('variables', operation.variables);

            // Log the operation extensions (if any)
            for (let key in operation.extensions && operation.extensions) scope.setExtra(key, operation.extensions[key]);

            // Log the graphQLErrors individually (if any)
            if (graphQLErrors) {
                for (let i = 0; i < graphQLErrors.length; i++) {
                    const {message, locations, path, extensions} = graphQLErrors[i];

                    // Ignore 401 errors to clean up the Sentry dashboard
                    if (message === 'You are not authorized to make this call.') continue;

                    Sentry.captureMessage(`ApolloError: ${message}`, {
                        extra: {
                            locations,
                            path,
                            extensions
                        }
                    })
                }
            }

            // Log the networkError (if any)
            if (networkError) Sentry.captureException(networkError);
        });
    }
}

// https://www.apollographql.com/docs/react/data/error-handling/#retrying-operations
const unauthorizedErrorLink = onError(({graphQLErrors, networkError, operation, forward}) => {
    logSentry({graphQLErrors, networkError, operation, forward});

    if (networkError?.name !== 'ServerError') return;

    const {statusCode} = networkError as ServerError;

    if (statusCode !== 401) return;

    return new Observable(observer => {
        // Request a new token and add it to the operation
        webService.identity.token()
            .then(token => operation.setContext(({headers = {}}) => ({
                headers: {
                    // Re-add old headers
                    ...headers,
                    // Switch out old access token for new one
                    authorization: token
                }
            })))
            // Retry last failed request
            .then(() => {
                forward(operation).subscribe({
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer)
                });
            })
            // But if token refresh fails, remove the token
            .catch(() => tokenService.remove());
    });
});

const authApolloLink = setContext((_, {headers}) => {
    // get the authentication token from local storage if it exists
    const token = tokenService.get();

    // return the headers to the context so httpLink can read them
    return {
        headers: {
            ...headers,
            authorization: token ? token : undefined
        }
    }
});

const contentServiceHttpLink = createHttpLink({uri: `https://${contentServiceSubdomain}.appsync-api.us-east-1.amazonaws.com/graphql`});
const erpServiceHttpLink = createHttpLink({uri: `https://${erpServiceSubdomain}.appsync-api.us-east-1.amazonaws.com/graphql`});

const contentServiceApolloLink = from([unauthorizedErrorLink, authApolloLink.concat(contentServiceHttpLink)]);
const erpServiceApolloLink = from([unauthorizedErrorLink, authApolloLink.concat(erpServiceHttpLink)]);

// noinspection GraphQLUnresolvedReference
export const apolloClient = new ApolloClient({
    cache: new InMemoryCache({
        typePolicies: {
            ComparisonItem: {
                keyFields: ['productId']
            },
            ComparisonSpecification: {
                keyFields: ['typeId']
            },
            ComparisonTable: {
                keyFields: ['id', 'productId', 'primaryId', 'currencyCode']
            },
            ComparisonValue: {
                keyFields: ['valueId']
            },
            ContactGroup: {
                keyFields: ['type']
            },
            CrossSell: {
                keyFields: ['productId']
            },
            ItemImages: {
                keyFields: ['itemId', 'itemType']
            },
            ItemMedia: {
                keyFields: ['itemId', 'itemType']
            },
            ItemQuestions: {
                keyFields: ['id', 'type']
            },
            ItemRatings: {
                keyFields: ['id', 'type']
            },
            ItemReviewMedia: {
                keyFields: ['id', 'type']
            },
            ItemUserContent: {
                keyFields: ['id', 'type']
            },
            MediaConnection: {
                fields: {
                    media: {
                        merge(existing: Nullable<ReviewMediaType>[] = [], incoming: Nullable<ReviewMediaType>[] = []) {
                            return [...existing, ...incoming.filter(incomingMedia => !existing.some(existingMedia => existingMedia?.id === incomingMedia?.id))];
                        }
                    }
                }
            },
            MediaEdge: {
                keyFields: ["cursor"]
            },
            ProductOptionValueItem: {
                keyFields: ['productId']
            },
            ProductSpecifications: {
                keyFields: ["productId"]
            },
            Promotion: {
                keyFields: ["campaign"]
            },
            Query: {
                fields: {
                    getProduct: {
                        merge: true
                    }
                }
            },
            QuestionConnection: {
                keyFields: ['id', 'type'],
                fields: {
                    edges: {
                        merge(existing: Nullable<{ __ref: string }>[] = [],
                              incoming: Nullable<{ __ref: string }>[] = []) {
                            return [...existing, ...incoming.filter(incomingEdge => !existing.some(existingEdge => existingEdge?.__ref === incomingEdge?.__ref))];
                        }
                    }
                }
            },
            QuestionEdge: {
                keyFields: ["cursor"]
            },
            QuestionStatistics: {
                keyFields: ['id', 'type']
            },
            RatingConnection: {
                keyFields: ['id', 'type']
            },
            RatingDistribution: {
                keyFields: ['id', 'type']
            },
            RatingEdge: {
                keyFields: ["cursor"]
            },
            RatingPagination: {
                keyFields: ['id', 'type'],
                fields: {
                    ratings: {
                        merge(existing: Nullable<{ __ref: string }>[] = [],
                              incoming: Nullable<{ __ref: string }>[] = [],
                              {variables}) {
                            // attempt to get the current page number from the incoming variables
                            const pageNumber = variables ? variables.pageNumber : 0;

                            // if this is the first page, return the incoming array as is (no need to merge)
                            if (pageNumber === 0) return incoming;

                            // this is not the first page, so merge the incoming with the existing (remove duplicates)
                            return [...existing, ...incoming.filter(incomingRating => !existing.some(existingRating => existingRating?.__ref === incomingRating?.__ref))];
                        }
                    }
                }
            },
            RatingStatistics: {
                keyFields: ['id', 'type']
            },
            Recommendation: {
                keyFields: ['productId']
            },
            RecommendationConnection: {
                fields: {
                    products: {
                        merge(existing: Nullable<RecommendedProductType>[] = [], incoming: Nullable<RecommendedProductType>[] = []) {
                            return [...existing, ...incoming.filter(incomingProduct => !existing.some(existingProduct => existingProduct?.productId === incomingProduct?.productId))];
                        }
                    }
                }
            },
            ReviewMediaConnection: {
                keyFields: ['id', 'type']
            },
            VariantAttribute: {
                keyFields: ['key', 'value']
            },
            YouTubeThumbnail: {
                keyFields: ['url']
            }
        }
    }),
    link: ApolloLink.split(({getContext}) => getContext()?.clientName === ApolloClients.ErpService, erpServiceApolloLink, contentServiceApolloLink),
    name: 'commercePlatformProductsFrontEnd',
    version: isDevelopmentBuild ? 'Alpha - v.1.00.00.001' : isPreProd ? 'RC - v.1.00.0.0' : 'v.1.00.0.0'
});